import { baseUrl } from '../../env';
import { DateTime } from 'luxon';
import axios from 'axios';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import LoadingModal from '@/components/LoadingModal.vue';
import { PROPOSER_CATEGORIES, proposerLabel } from '@/utils/proposerCategories';

export default {
  name: 'CommissionPage',
  components: {
    VueDatePicker,
    LoadingModal
  },

  data() {
    return {
      filteredData: [],
      townships: [],
      categories: PROPOSER_CATEGORIES,
      // Always an ARRAY because <v-select multiple> is used
      selectedTownship: [],
      checkedOrganizations: [],
      date: { from: null, to: null },
      loading: false,
      loadingLabel: 'Учитавање резултата...',
      data: [],
      // simple sorting state
      sortKey: 'organization',
      sortOrder: 'asc', // 'asc' | 'desc'
      organizations: []
    };
  },

  watch: {
    // refetch on route change
    $route: 'fetchData',
  },

  created() {
    // prefill dates from query
    this.date.from = this.$route.query.od
      ? DateTime.fromFormat(this.$route.query.od, 'dd.MM.yyyy.').toJSDate()
      : null;
    this.date.to = this.$route.query.do
      ? DateTime.fromFormat(this.$route.query.do, 'dd.MM.yyyy.').toJSDate()
      : null;

    this.checkedOrganizations = this.$route.query.organizacije
      ? this.$route.query.organizacije.split(',')
      : [];

    this.fetchData();
  },
  computed: {
    // First column header follows the applied (submitted) category filter
    proposerColumnLabel() {
      const applied = this.$route.query.organizacije
        ? this.$route.query.organizacije.split(',')
        : [];
      const selected = this.categories.filter(c => applied.includes(c.id));

      if (selected.length === 0 || selected.length === this.categories.length) {
        return 'Предлагач';
      }
      return selected.map(c => c.name).join(' / ');
    },

    normalizedData() {
      return this.data.map(contest => ({
        ...contest,
        commissionMembers: (contest.commissionMembers || []).map(m => ({
          ...m,
          name: m.member_name || m.name || '', // normalize key
          suggested: !!m.suggested,
          chosen: !!m.chosen
        }))
      }));
    },

    hasChosen() {
      return this.normalizedData.reduce((acc, contest) => {
        const chosen = (contest.commissionMembers || []).filter(m => m.chosen);
        return acc + chosen.length;
      }, 0);
    },

    hasSuggested() {
      return this.normalizedData.reduce((acc, contest) => {
        const suggested = (contest.commissionMembers || []).filter(m => m.suggested);
        return acc + suggested.length;
      }, 0);
    },

    sortedData() {
      const list = [...this.filteredData];
      if (!this.sortKey) return list;

      const order = this.sortOrder === 'asc' ? 1 : -1;

      return list.sort((a, b) => {
        let valA, valB;

        switch (this.sortKey) {
          case 'organization':
            valA = this.proposerName(a);
            valB = this.proposerName(b);
            return order * String(valA).localeCompare(String(valB), 'sr');

          case 'township':
            valA = a?.township?.name || '';
            valB = b?.township?.name || '';
            return order * String(valA).localeCompare(String(valB), 'sr');

          case 'commissionMembers':
            valA = (a?.commissionMembers || []).length;
            valB = (b?.commissionMembers || []).length;
            return order * (valA - valB);

          case 'chosenMembers':
            valA = (a?.commissionMembers || []).filter(m => m.chosen).length;
            valB = (b?.commissionMembers || []).filter(m => m.chosen).length;
            return order * (valA - valB);

          case 'date_money': {
            const dA = a?.date_money ? DateTime.fromISO(a.date_money).toMillis() : 0;
            const dB = b?.date_money ? DateTime.fromISO(b.date_money).toMillis() : 0;
            return order * (dA - dB);
          }

          default: {
            // generic fallback
            valA = a?.[this.sortKey] ?? '';
            valB = b?.[this.sortKey] ?? '';
            // numeric compare if both numbers
            if (typeof valA === 'number' && typeof valB === 'number') {
              return order * (valA - valB);
            }
            return order * String(valA).localeCompare(String(valB), 'sr');
          }
        }
      });
    }
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const tRes = await axios.get(`${baseUrl}/townships`);
        const towns = tRes.data || [];
        towns.unshift({ name: 'Sve jedinice', id: 'all' });
        this.townships = towns;

        const q = this.$route.query.jedinica;
        if (q) {
          if (q === 'all') {
            const allOpt = towns.find(t => String(t.id) === 'all') || { id: 'all', name: 'Sve jedinice' };
            this.selectedTownship = [allOpt];
          } else {
            const ids = q.split(',');
            this.selectedTownship = towns.filter(t => ids.includes(String(t.id)));
          }
        }

        const jedinica = this.$route.query.jedinica;
        const from = this.$route.query.od || '01.01.1971.';
        const to = this.$route.query.do || DateTime.local().toFormat('dd.MM.yyyy.');

        const url = jedinica
          ? `${baseUrl}/commission_members/${jedinica}/${from}/${to}/${(this.checkedOrganizations || []).join(',')}`
          : null;

        let response = null;
        if (url) response = await axios.get(url);

        this.data = response ? response.data : {};

        // ✅ FLATTEN NEW back-end structure
        const flat = [];

        for (const org of Object.values(this.data)) {
          for (const t of org.contests) {
            for (const c of t.contests) {
              flat.push({
                contestId: c.contestid,
                organization: c.organization,
                township: t.township,
                commissionMembers: c.commission_members || [],
                date_money: c.calendar
              });
            }
          }
        }

        this.filteredData = flat;

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.loading = false;
      }
    }
    ,

    submit() {
      // Build query from current selections
      const from = this.date.from && DateTime.fromJSDate(this.date.from).toFormat('dd.MM.yyyy.');
      const to = this.date.to && DateTime.fromJSDate(this.date.to).toFormat('dd.MM.yyyy.');

      // selectedTownship is always an array (because of <v-select multiple>)
      const sel = Array.isArray(this.selectedTownship) ? this.selectedTownship : [];

      // If "all" is selected among options, send 'all'
      const hasAll = sel.some(s => s && (s.id === 'all' || s === 'all')) || (sel.length === 0);

      const jedinica = hasAll
        ? 'all'
        : sel.map(s => s.id).join(',');

      const query = {};
      if (jedinica && jedinica.length) query.jedinica = jedinica;
      if (from) query.od = from;
      if (to) query.do = to;
      if (this.checkedOrganizations.length > 0) {
        query.organizacije = this.checkedOrganizations.join(',');
      }

      this.$router.push({ query });
    },
    proposerName(contest) {
      return proposerLabel(contest?.organization?.category_id, contest?.organization?.name);
    },

    normalizeName(m) {
      return { ...m, name: m.name || m.member_name || '' };
    },
    getSuggestedMembers(contest) {
      const members = (contest?.commissionMembers || [])
        .filter(m => m.suggested)
        .map(this.normalizeName);

      // Remove duplicates based on `name` only
      const seen = new Set();
      return members.filter(m => {
        if (seen.has(m.name)) return false;
        seen.add(m.name);
        return true;
      });
    },

    getChosenMembers(contest) {
      const members = (contest?.commissionMembers || [])
        .filter(m => m.chosen)
        .map(this.normalizeName);

      // Remove duplicates based on `name` only
      const seen = new Set();
      return members.filter(m => {
        if (seen.has(m.name)) return false;
        seen.add(m.name);
        return true;
      });
    },

    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },

    parseDate2(d) {
      if (!d) return new Date(0);
      const [day, month, year] = d.split('.');
      return new Date(`${year}-${month}-${day}`);
    },

    // Expects "dd.MM.yyyy." or "dd.MM.yyyy"
    parseDate(str) {
      if (!str || typeof str !== 'string') return new Date(0);
      const parts = str.split('.').filter(Boolean); // drop trailing empty after last dot
      const [day, month, year] = parts;
      if (!day || !month || !year) return new Date(0);
      return new Date(`${year}-${month}-${day}`);
    },

    formatDate(str) {
      const d = this.parseDate(str);
      if (isNaN(d)) return '—';
      return d.toLocaleDateString('sr-RS');
    },

    formatDateFront(dateStr) {
      if (!dateStr) return '';
      return DateTime.fromISO(dateStr)
        .setZone('Europe/Belgrade')
        .toFormat('dd.MM.yyyy');
    }
  }
};
